import { Streamdown } from 'streamdown'
import { cn } from '@/lib/utils'
import { Check, Copy } from 'lucide-react'
import { useState, isValidElement, type ComponentPropsWithoutRef, type ReactNode } from 'react'

interface MarkdownProps {
  children: string
  className?: string
}

// Copy button component for code blocks
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={handleCopy}
      className="absolute top-2 right-2 p-2 rounded-md bg-muted hover:bg-muted/80 transition-colors"
      title="Copy code"
    >
      {copied ? (
        <Check className="w-4 h-4 text-green-500" />
      ) : (
        <Copy className="w-4 h-4 text-muted-foreground" />
      )}
    </button>
  )
}

export function Markdown({ children, className }: MarkdownProps) {
  return (
    <Streamdown
      className={cn('prose prose-sm dark:prose-invert max-w-none break-words', className)}
      parseIncompleteMarkdown={true}
      shikiTheme={['github-dark', 'github-dark']}
      components={{
        // Custom code block with copy button
        pre({ children, ...props }: ComponentPropsWithoutRef<'pre'>) {
          // Extract the code text for copying (children is the inner <code> element)
          const codeText = isValidElement<{ children?: ReactNode }>(children)
            ? children.props.children
            : ''

          return (
            <div className="relative group my-4 max-w-full overflow-hidden">
              <pre className="overflow-x-auto rounded-lg bg-muted p-4 max-w-full" {...props}>
                {children}
              </pre>
              <CopyButton text={String(codeText)} />
            </div>
          )
        },

        // Inline code
        code({ inline, children, ...props }: ComponentPropsWithoutRef<'code'> & { inline?: boolean }) {
          if (inline) {
            return (
              <code
                className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono break-all"
                {...props}
              >
                {children}
              </code>
            )
          }
          return (
            <code className="break-all" {...props}>
              {children}
            </code>
          )
        },

        // Paragraphs
        p({ children }: ComponentPropsWithoutRef<'p'>) {
          return <p className="mb-4 last:mb-0 leading-relaxed break-words">{children}</p>
        },

        // Lists
        ul({ children }: ComponentPropsWithoutRef<'ul'>) {
          return <ul className="list-disc list-inside mb-4 space-y-1 ml-4">{children}</ul>
        },
        ol({ children }: ComponentPropsWithoutRef<'ol'>) {
          return <ol className="list-decimal list-inside mb-4 space-y-1 ml-4">{children}</ol>
        },
        li({ children }: ComponentPropsWithoutRef<'li'>) {
          return <li className="ml-2">{children}</li>
        },

        // Links
        a({ href, children }: ComponentPropsWithoutRef<'a'>) {
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline hover:text-primary/80 transition-colors"
            >
              {children}
            </a>
          )
        },

        // Headings
        h1({ children }: ComponentPropsWithoutRef<'h1'>) {
          return <h1 className="text-2xl font-bold mb-4 mt-6 first:mt-0">{children}</h1>
        },
        h2({ children }: ComponentPropsWithoutRef<'h2'>) {
          return <h2 className="text-xl font-bold mb-3 mt-5 first:mt-0">{children}</h2>
        },
        h3({ children }: ComponentPropsWithoutRef<'h3'>) {
          return <h3 className="text-lg font-bold mb-2 mt-4 first:mt-0">{children}</h3>
        },
        h4({ children }: ComponentPropsWithoutRef<'h4'>) {
          return <h4 className="text-base font-bold mb-2 mt-3 first:mt-0">{children}</h4>
        },

        // Blockquote
        blockquote({ children }: ComponentPropsWithoutRef<'blockquote'>) {
          return (
            <blockquote className="border-l-4 border-border pl-4 italic my-4 text-muted-foreground">
              {children}
            </blockquote>
          )
        },

        // Tables
        table({ children }: ComponentPropsWithoutRef<'table'>) {
          return (
            <div className="my-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-border">{children}</table>
            </div>
          )
        },
        thead({ children }: ComponentPropsWithoutRef<'thead'>) {
          return <thead className="bg-muted">{children}</thead>
        },
        th({ children }: ComponentPropsWithoutRef<'th'>) {
          return <th className="px-4 py-2 text-left text-sm font-semibold">{children}</th>
        },
        td({ children }: ComponentPropsWithoutRef<'td'>) {
          return <td className="px-4 py-2 text-sm border-t border-border">{children}</td>
        },
      }}
    >
      {children}
    </Streamdown>
  )
}
